import { useState, useEffect, useRef } from 'react';
import { useData } from '../../../contexts/DataContext';
import { apiUtils } from '../../../utils/apiUtils';
import ScanService from '../../../services/ScanService';

/**
 * useClientDetail - Business logic hook for client detail page
 * 
 * Handles fetching and managing client and account data.
 * Follows separation of concerns by keeping business logic separate from UI.
 */
export const useClientDetail = (clientId) => {
	const { fetchAccountsByClient, error } = useData();

	const [client, setClient] = useState(null);
	const [accounts, setAccounts] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedPlatform, setSelectedPlatform] = useState('');
	const [localLoading, setLocalLoading] = useState(false);
	const [clientError, setClientError] = useState(null);
	const isMountedRef = useRef(true);

	// Set mounted ref on mount/unmount
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Fetch client and accounts data
	useEffect(() => {
		let cancelled = false;

		const fetchData = async () => {
			if (!clientId) return;

			try {
				setLocalLoading(true);
				setClientError(null);
				const [clientResp, accountsData] = await Promise.all([
					// A missing or forbidden client used to be swallowed here, which left the
					// page rendering an empty body with no explanation at all.
					apiUtils.get(`/api/clients/${clientId}`).catch((requestError) => {
						if (!cancelled && isMountedRef.current) setClientError(requestError?.response?.status === 404
							? 'This client does not exist, or you no longer have access to it.'
							: 'We could not load this client. Please try again.');
						return null;
					}),
					fetchAccountsByClient(clientId),
				]);

				// Only update state if component is still mounted and request wasn't cancelled
				if (!cancelled && isMountedRef.current) {
					setAccounts(accountsData || []);

					// Prefer the real client record; fall back to the account-embedded client.
					const realClient = clientResp?.data?.data?.client || clientResp?.data?.data || clientResp?.data;
					if (realClient && (realClient.name || realClient._id)) {
						setClient(realClient);
					} else if (accountsData && accountsData.length > 0 && accountsData[0].client) {
						setClient(accountsData[0].client);
					} else {
						setClient({ _id: clientId, name: 'Client' });
					}
				}
			} catch (err) {
				if (!cancelled && isMountedRef.current) {
					console.error('Error fetching client data:', err);
					setAccounts([]);
				}
			} finally {
				if (!cancelled && isMountedRef.current) {
					setLocalLoading(false);
				}
			}
		};

		fetchData();

		// Cleanup function to cancel the request
		return () => {
			cancelled = true;
		};
	}, [clientId, fetchAccountsByClient]);

	// Filter accounts based on search and platform
	const filteredAccounts = (accounts || []).filter(account => {
		const matchesSearch = !searchTerm ||
			account.handle.toLowerCase().includes(searchTerm.toLowerCase()) ||
			(account.displayName && account.displayName.toLowerCase().includes(searchTerm.toLowerCase())) ||
			(account.bio && account.bio.toLowerCase().includes(searchTerm.toLowerCase()));

		const matchesPlatform = !selectedPlatform || account.platform === selectedPlatform;

		return matchesSearch && matchesPlatform;
	});

	// Refresh accounts data
	const refreshAccounts = async () => {
		if (!clientId || !isMountedRef.current) return;

		try {
			setLocalLoading(true);
			const accountsData = await fetchAccountsByClient(clientId);
			if (isMountedRef.current) {
				setAccounts(accountsData || []);
			}
		} catch (err) {
			if (isMountedRef.current) {
				console.error('Error refreshing accounts:', err);
				setAccounts([]);
			}
		} finally {
			if (isMountedRef.current) {
				setLocalLoading(false);
			}
		}
	};

	// Update search term
	const updateSearch = (term) => {
		setSearchTerm(term);
	};

	// Update platform filter
	const updatePlatformFilter = (platform) => {
		setSelectedPlatform(platform);
	};

	// Reset filters
	const resetFilters = () => {
		setSearchTerm('');
		setSelectedPlatform('');
	};

	// Derived - offering Instagram/Facebook/LinkedIn here filtered on platforms
	// that can never have results, and omitted Reddit, which can.
	const platformOptions = [
		{ value: '', label: 'All Platforms' },
		...ScanService.getEnabledPlatforms().map((p) => ({ value: p.short, label: p.label })),
	];

	// Helper functions
	const formatDate = (date) => {
		if (!date) return "Never";
		return new Date(date).toLocaleDateString("en-US", {
			month: "2-digit",
			day: "2-digit",
			year: "numeric",
		});
	};

	const formatNumber = (num) => {
		if (!num) return "0";
		if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
		if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
		return num.toString();
	};

	const getRiskColor = (risk) => {
		if (risk >= 70) return "text-red-600 font-bold";
		if (risk >= 40) return "text-orange-600 font-medium";
		return "text-green-600";
	};

	return {
		// Data
		client,
		accounts,
		filteredAccounts,
		searchTerm,
		selectedPlatform,
		platformOptions,
		loading: localLoading,
		error: clientError || error,

		// Actions
		refreshAccounts,
		updateSearch,
		updatePlatformFilter,
		resetFilters,

		// Helpers
		formatDate,
		formatNumber,
		getRiskColor,

		// Computed
		isEmpty: (accounts || []).length === 0,
		isFiltered: searchTerm || selectedPlatform,
		hasFilters: searchTerm || selectedPlatform,
	};
}; 
